import type { LocalDate } from './date';
import {
  DEFAULT_DAILY_NEW_LIMIT,
  DEFAULT_DAILY_REVIEW_LIMIT,
  type ProgressState,
  type Settings,
} from './types';

/** キューに必要なカードの情報。cards はカード順（card-schema の順。cards の sortCards）で渡す。 */
export interface QueueCard {
  id: string;
  draft?: boolean;
}

export interface DailyQueueOptions {
  /**
   * 新規に出さないカード（記録の無いカードだけに効く）。既定は無し（off）。
   * レッスンを完了するまで紐づくカードを止めるときは heldNewCards（./lessons）の結果を渡す。
   */
  holdNew?: ReadonlySet<string>;
}

/**
 * 今日のキュー（progress-store.md §4.4）。復習 → 新規。
 * 復習: 記録があり due <= today、draft でない。due 昇順 → box 昇順 → カード順。先頭 dailyReviewLimit 件。
 * 新規: 記録が無く draft でなく、options.holdNew に無い。dailyNewLimit − 今日 firstSeen になった数。
 */
export function buildDailyQueue(
  state: ProgressState,
  cards: readonly QueueCard[],
  today: LocalDate,
  settings: Settings,
  options: DailyQueueOptions = {},
): string[] {
  const reviewLimit = settings.dailyReviewLimit ?? DEFAULT_DAILY_REVIEW_LIMIT;
  const newLimit = settings.dailyNewLimit ?? DEFAULT_DAILY_NEW_LIMIT;
  const review: { id: string; due: string; box: number; order: number }[] = [];
  const fresh: string[] = [];
  cards.forEach((c, order) => {
    if (c.draft) return;
    const rec = state.cards[c.id];
    if (rec) {
      if (rec.due <= today) review.push({ id: c.id, due: rec.due, box: rec.box, order });
    } else if (!options.holdNew?.has(c.id)) {
      fresh.push(c.id);
    }
  });
  review.sort((a, b) =>
    a.due < b.due ? -1 : a.due > b.due ? 1 : a.box - b.box || a.order - b.order,
  );
  const introducedToday = Object.values(state.cards).filter((r) => r.firstSeen === today).length;
  const newCount = Math.max(0, newLimit - introducedToday);
  return [...review.slice(0, reviewLimit).map((r) => r.id), ...fresh.slice(0, newCount)];
}

/** 再出題の間隔（§4.4 手順 4）。 */
export const REQUEUE_GAP = 3;

/**
 * セッション内で不正解だったカードを、現在位置の 3 枚後ろ（残りが 3 枚未満なら末尾）に入れ直す。
 * メモリ上だけで保存しない。currentIndex は不正解だったカードの位置。
 */
export function requeueWrong(queue: readonly string[], currentIndex: number): string[] {
  const id = queue[currentIndex];
  if (id === undefined) return [...queue];
  const out = [...queue];
  const remaining = queue.length - currentIndex - 1;
  if (remaining < REQUEUE_GAP) out.push(id);
  else out.splice(currentIndex + 1 + REQUEUE_GAP, 0, id);
  return out;
}
